import { useCallback, useMemo } from 'react'

import SpecialStatusAlert from '@helpscout/hsds-icons/special-status-alert'
import SpecialStatusCheck from '@helpscout/hsds-icons/special-status-check'

import {
  STATUS_ERROR,
  STATUS_MESSAGE,
  STATUS_SUCCESS,
  STATUS_WARNING,
} from '../Noty.constants'
import {
  ADD,
  HIDE_CONFIRM,
  REMOVE,
  REMOVE_ALL,
  SHOW_CONFIRM,
  createNotyId,
  useNotyReducer,
} from './useNotyReducer'

/**
 * @typedef {import('../Noty.constants').TNotyItem} TNotyItem
 * @typedef {import('../Noty.constants').TNotyConfirm} TNotyConfirm
 */

/**
 * @typedef TConfirmOpts
 * @property {function} onConfirm
 * @property {function} onClose
 * @property {boolean} [danger]
 * @property {string} [primaryButtonText]
 * @property {string} [secondaryButtonText]
 * @property {any} [body]
 * @property {number} [modalWidth]
 * @property {number} [modalHeight]
 * @property {boolean} [closeOnConfirm]
 * @property {boolean} [fixedPosition]
 */

/**
 * @typedef TNotyOpts
 * @property {number} [timeout] Pauses while the toast is hovered or has focus, unless `expires` is set.
 * @property {boolean} [expires] The button action stops working after `timeout`, e.g. Undo send. The toast then closes on time and does not pause on hover or focus.
 * @property {React.ElementType} [icon]
 * @property {TNotyPrimaryButtonOpts} [primaryButtonProps]
 * @property {TNotyPrimaryButtonOpts} [dangerButtonProps] Pass Undo here: the newest toast with its `onClick` gets the Z hotkey.
 * @property {boolean} [urgent] Announce assertively even when the status is polite, e.g. a warning that reports a failed action.
 */

/**
 * @typedef TNotyPrimaryButtonOpts
 * @property {string} label
 * @property {function=} onClick
 * Optional: a noty action can be a button (onClick) or a link (href). Noty
 * renders whichever is supplied — see the two primaryButtonProps cases in
 * Noty.test.js.
 * @property {string=} href
 * @property {string=} rel
 * @property {string=} target
 */

/**
 * @callback actionCallback
 * @param {string|ReactElement} msg Text and formatting only: it is also rendered in a hidden live region, where a link or button would be a duplicate control. Use the button options for actions.
 * @param {TNotyOpts} [opts]
 */

/**
 * @callback confirmCallback
 * @param {string} msg
 * @param {TConfirmOpts} opts
 */

/**
 *
 * @callback hideCallback
 * @param {number} id
 */

/**
 * @typedef TNotyContext
 * @property {function} hideAllNotys
 * @property {function} hideConfirmNoty
 * @property {hideCallback} hideNoty
 * @property {confirmCallback} showConfirmNoty
 * @property {actionCallback} showErrorNoty
 * @property {actionCallback} showMessageNoty
 * @property {actionCallback} showSuccessNoty
 * @property {actionCallback} showWarningNoty
 *
 */

/**
 * Noty hook to initialize the reducer and create all callbacks that will be serve in `useNoty`
 * @returns {{notys: Array.<TNotyItem>, confirm: TNotyConfirm, contextValue: TNotyContext}}
 */
export const useNotyCore = () => {
  const [notys, confirm, dispatch] = useNotyReducer()
  const hideConfirmNoty = useCallback(() => {
    dispatch({
      type: HIDE_CONFIRM,
    })
  }, [dispatch])
  const hideNoty = useCallback(
    /**
     * @type {hideCallback}
     */
    id => {
      dispatch({
        type: REMOVE,
        id,
      })
    },
    [dispatch]
  )
  const showConfirmNoty = useCallback(
    /**
     * @type {confirmCallback}
     */
    (title, opts = {}) => {
      dispatch({
        ...opts,
        type: SHOW_CONFIRM,
        title,
      })

      return [hideConfirmNoty]
    },
    [dispatch, hideConfirmNoty]
  )

  const showErrorNoty = useCallback(
    /**
     * @type {actionCallback}
     * @returns {[hideCallback, number]}
     */
    (msg, opts = {}) => {
      const id = createNotyId()
      dispatch({
        opts: {
          timeout: 10000,
          icon: SpecialStatusAlert,
          ...opts,
        },
        type: ADD,
        status: STATUS_ERROR,
        content: msg,
        id,
      })

      const hide = () => {
        hideNoty(id)
      }
      return [hide, id]
    },
    [dispatch, hideNoty]
  )
  const showSuccessNoty = useCallback(
    /**
     * @type {actionCallback}
     * @returns {[hideCallback, number]}
     */
    (msg, opts = {}) => {
      const id = createNotyId()

      dispatch({
        opts: {
          timeout: 8000,
          icon: SpecialStatusCheck,
          ...opts,
        },
        type: ADD,
        status: STATUS_SUCCESS,
        content: msg,
        id,
      })

      const hide = () => {
        hideNoty(id)
      }
      return [hide, id]
    },
    [dispatch, hideNoty]
  )
  const showMessageNoty = useCallback(
    /**
     * @type {actionCallback}
     * @returns {[hideCallback, number]}
     */
    (msg, opts = {}) => {
      const id = createNotyId()

      dispatch({
        opts: {
          timeout: 10000,
          ...opts,
        },
        type: ADD,
        status: STATUS_MESSAGE,
        content: msg,
        id,
      })

      const hide = () => {
        hideNoty(id)
      }
      return [hide, id]
    },
    [dispatch, hideNoty]
  )
  const showWarningNoty = useCallback(
    /**
     * @type {actionCallback}
     * @returns {[hideCallback, number]}
     */
    (msg, opts = {}) => {
      const id = createNotyId()
      dispatch({
        opts: {
          ...opts,
        },
        type: ADD,
        status: STATUS_WARNING,
        content: msg,
        id,
      })

      const hide = () => {
        hideNoty(id)
      }
      return [hide, id]
    },
    [dispatch, hideNoty]
  )
  const hideAllNotys = useCallback(() => {
    dispatch({
      type: REMOVE_ALL,
    })
  }, [dispatch])

  /**
   *
   * @typedef TNotyContext
   * @property {function} hideAllNotys
   * @property {function} hideConfirmNoty
   * @property {function} hideNoty
   * @property {function} showConfirmNoty
   * @property {function} showErrorNoty
   * @property {function} showMessageNoty
   * @property {function} showSuccessNoty
   * @property {function} showWarningNoty
   *
   * @type {TNotyContext} contextValue
   */
  const contextValue = useMemo(() => {
    return {
      hideAllNotys,
      hideConfirmNoty,
      hideNoty,
      showConfirmNoty,
      showErrorNoty,
      showMessageNoty,
      showSuccessNoty,
      showWarningNoty,
    }
  }, [
    hideAllNotys,
    hideConfirmNoty,
    hideNoty,
    showConfirmNoty,
    showErrorNoty,
    showMessageNoty,
    showSuccessNoty,
    showWarningNoty,
  ])

  return {
    notys,
    confirm,
    contextValue,
  }
}
